import {
	ChangeDetectionStrategy,
	Component,
	Input,
	OnInit,
	DestroyRef
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import Order from 'src/app/models/Order';
import {Venue} from 'src/app/models/Venue';
import {CountdownService} from 'src/app/services/countdown.service';
import {isUber, isWolt} from '../manager-utils/manager-utils';
import * as moment from 'moment';
import {BehaviorSubject} from 'rxjs';
import {ApiService} from 'src/app/services/api.service';
import {SnackbarService} from 'src/app/services/snackbar.service';

export type UberCancelReason =
	| 'ITEM_ISSUE'
	| 'KITCHENCLOSED'
	| 'CUSTOMER_CALLED_TO_CANCEL'
	| 'RESTAURANT_TOO_BUSY'
	| 'ORDER_VALIDATION'
	| 'STORE_CLOSED'
	| 'TECHNICAL_FAILURE'
	| 'POS_NOT_READY'
	| 'POS_OFFLINE'
	| 'CAPACITY'
	| 'ADDRESS'
	| 'SPECIAL_INSTRUCTIONS'
	| 'PRICING'
	| 'UNKNOWN'
	| 'OTHER';

const UBER_WINDOW = 10;
const WOLT_WINDOW = 2;

@Component({
	selector: 'app-manual-acceptance-order',
	templateUrl: './manual-acceptance-order.html',
	styleUrls: ['./manual-acceptance-order.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ManualAcceptanceOrder implements OnInit {
	timeLeft$ = new BehaviorSubject(0);
	alarmMode$ = new BehaviorSubject(false);
	timeWindow = 0;

	timeOptions = [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60];

	preparationTime = 30;
	deliveryTime = 30;

	@Input() order: Order;
	@Input() venue: Venue;

	constructor(
		private apiService: ApiService,
		private countdownService: CountdownService,
		private destroyRef: DestroyRef,
		private snackbarService: SnackbarService
	) {}

	ngOnInit(): void {
		this.getAdditionalTime();
		this.getTimeLeft();
	}

	getTimeLeft() {
		const targetTime = moment(this.order.createdAt).add(
			this.timeWindow,
			'minutes'
		);

		this.countdownService
			.getCountdown(targetTime)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(timeLeft => {
				this.timeLeft$.next(timeLeft);
				if (timeLeft / 60 <= 3) {
					this.alarmMode$.next(true);
				}
			});
	}

	getAdditionalTime() {
		let time = 0;

		if (isWolt(this.order)) {
			time = WOLT_WINDOW;
		}
		if (isUber(this.order)) {
			time = UBER_WINDOW;
		}

		this.timeWindow = time;
	}

	formatTime(seconds: number): string {
		const abs = Math.abs(seconds);
		const m = Math.floor(abs / 60);
		const s = abs % 60;
		return `${seconds < 0 ? '-' : ''}${m}:${s < 10 ? '0' : ''}${s}`;
	}

	accept() {
		if (isWolt(this.order)) {
			this.apiService
				.acceptWoltManually(this.order.venue, this.order.externalOrderId, {
					preparationTime: this.preparationTime,
					deliveryTime: this.deliveryTime
				})
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(this.handleSuccess('Wolt order accepted'));
		}

		if (isUber(this.order)) {
			this.apiService
				.acceptUberManually(this.order.venue, this.order.externalOrderId, {
					preparationTime: this.preparationTime
				})
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(this.handleSuccess('Uber order accepted'));
		}
	}

	reject() {
		if (isWolt(this.order)) {
			this.apiService
				.rejectWoltManually(this.order.venue, this.order.externalOrderId, {
					reason: 'RESTAURANT_TOO_BUSY'
				})
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(this.handleSuccess('Wolt order rejected'));
		}

		if (isUber(this.order)) {
			this.apiService
				.rejectUberManually(this.order.venue, this.order.externalOrderId, {
					info: 'Rejected by restaurant',
					type: 'RESTAURANT_TOO_BUSY'
				})
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(this.handleSuccess('Uber order cancelled'));
		}
	}

	private handleSuccess(msg: string) {
		return {
			next: () => {
				this.snackbarService.showSuccess(msg);
			},
			error: (e: any) => {
				this.snackbarService.showError(
					e.error.message || 'Something went wrong...'
				);
			}
		};
	}
}
